import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { MODULE_KEYS, SUBMODULE_KEYS, getProductDefinition } from '@easyflow/shared';
import { ToastAction } from '@easyflow/ui-frontend/components/ui/toast';
import { apiClient } from '@/lib/api';
import { FRONTEND_PRODUCT } from '@/lib/product';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from './AuthContext';
import type { MyModulesResponse } from '@easyflow/shared';

interface ModuleContextType {
  enabledModules: string[];
  loading: boolean;
  error: boolean;
  /** Task #3148 — prodotto/edition del tenant (da /api/my-modules); null = sconosciuto/back-compat. */
  product: string | null;
  isModuleEnabled: (key: string) => boolean;
  refreshModules: () => Promise<void>;
}

const ModuleContext = createContext<ModuleContextType>({
  enabledModules: [],
  loading: true,
  error: false,
  product: null,
  isModuleEnabled: () => false,
  refreshModules: async () => {},
});

export function ModuleProvider({ children }: { children: React.ReactNode }) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [enabledModules, setEnabledModules] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [product, setProduct] = useState<string | null>(null);
  const isMaster = !!user?.is_master_tenant;
  // Avoid spamming the toast on each retry
  const hasShownErrorToast = useRef(false);

  const fetchModules = useCallback(async () => {
    if (!user) {
      setEnabledModules([]);
      setProduct(null);
      setError(false);
      setLoading(false);
      return;
    }

    // Master tenant bypasses module gating entirely (handled in isModuleEnabled).
    // We still seed `enabledModules` from the SSOT registry so any consumer
    // that reads the array directly (rather than calling `isModuleEnabled`)
    // sees a coherent list instead of being empty.
    if (isMaster) {
      setEnabledModules([...MODULE_KEYS, ...SUBMODULE_KEYS]);
      setError(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Risposta top-level (`{ success, data, product }`): `product` è un campo
      // sibling di `data`, non dentro l'envelope.
      const data = await apiClient.getTopLevel<MyModulesResponse>('/my-modules');
      if (data?.success && Array.isArray(data.data)) {
        setEnabledModules(data.data);
        // Task #3148 — campo sibling `product`; assente su backend pre-deploy
        // (back-compat: null = nessun guard).
        setProduct(typeof data.product === 'string' ? data.product : null);
        setError(false);
        hasShownErrorToast.current = false;
      } else {
        throw new Error('Invalid response from /api/my-modules');
      }
    } catch (err) {
      console.error('Failed to load modules:', err);
      // SAFE FALLBACK: do NOT enable everything. Keep the sidebar empty until
      // the user retries; otherwise a transient network error would silently
      // grant access to features the tenant has not paid for.
      setEnabledModules([]);
      setError(true);
      if (!hasShownErrorToast.current) {
        hasShownErrorToast.current = true;
        toast({
          title: 'Impossibile caricare i moduli',
          description: 'La barra laterale resterà vuota finché non si recupera la connessione.',
          variant: 'destructive',
          action: (
            <ToastAction
              altText="Riprova a caricare i moduli"
              onClick={() => { hasShownErrorToast.current = false; void fetchModules(); }}
            >
              Riprova
            </ToastAction>
          ),
        });
      }
    } finally {
      setLoading(false);
    }
  }, [user, isMaster, toast]);

  useEffect(() => {
    fetchModules();
  }, [fetchModules]);

  const isModuleEnabled = useCallback((key: string) => {
    // Master tenant always sees every module.
    if (isMaster) return true;
    // While loading we conservatively return false to avoid a flash of
    // restricted UI; the sidebar simply renders nothing for one render cycle.
    if (loading) return false;
    // Backward-compat aliases for legacy callers still referencing old keys
    // (Wave 2 unified `landing_pages`/`site_builder` into `web_builder`).
    const aliases: Record<string, string> = {
      landing_pages: 'web_builder',
      site_builder: 'web_builder',
    };
    const resolved = aliases[key] || key;
    return enabledModules.includes(resolved);
  }, [enabledModules, loading, isMaster]);

  // Task #3148 — guard prodotto: questa build serve SOLO FRONTEND_PRODUCT.
  // Un utente di un tenant con prodotto diverso (es. w3suite) non deve vedere
  // l'app: schermata di rifiuto con label dal registry condiviso. `null` =
  // backend pre-deploy o fetch fallita → nessun blocco (back-compat).
  if (user && !isMaster && !loading && product && product !== FRONTEND_PRODUCT) {
    const tenantProductLabel = getProductDefinition(product)?.label ?? product;
    const thisProductLabel = getProductDefinition(FRONTEND_PRODUCT)?.label ?? FRONTEND_PRODUCT;
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-4">
        <div className="max-w-md text-center space-y-3">
          <h1 className="text-xl font-semibold text-foreground">Applicazione non disponibile</h1>
          <p className="text-sm text-muted-foreground">
            Il tuo account appartiene a <strong>{tenantProductLabel}</strong>, mentre questa
            applicazione serve <strong>{thisProductLabel}</strong>. Accedi dall'applicazione
            del tuo prodotto oppure contatta l'amministratore.
          </p>
        </div>
      </div>
    );
  }

  return (
    <ModuleContext.Provider value={{ enabledModules, loading, error, product, isModuleEnabled, refreshModules: fetchModules }}>
      {children}
    </ModuleContext.Provider>
  );
}

export const useModules = () => useContext(ModuleContext);
